/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { useState, useEffect } from 'react';
import { clientsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Trash, Pencil } from '@phosphor-icons/react';
import { FileText, Download, Receipt, Settings, BookOpen } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const apiFetch = (url, opts = {}) => fetch(`${API}${url}`, { credentials: 'include', ...opts });
const apiJson = (url, method, body) => apiFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

export default function InvoicingPage() {
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [tvaRates, setTvaRates] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [invoiceDialog, setInvoiceDialog] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ client_id: '', period_start: '', period_end: '', nature: 'Prestation de service', delivery_address: '', comments: '', is_proforma: false });
  const [generating, setGenerating] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editLines, setEditLines] = useState([]);
  const [editComments, setEditComments] = useState('');
  const [tvaDialog, setTvaDialog] = useState(false);
  const [tvaForm, setTvaForm] = useState({ label: '', rate: '', is_default: false });
  const [accountDialog, setAccountDialog] = useState(false);
  const [accountForm, setAccountForm] = useState({ code: '', label: '', type: 'credit' });
  const [invoiceSettings, setInvoiceSettings] = useState({
    prefix: 'FAC', next_number: 1, payment_terms: 'Paiement a 30 jours fin de mois',
    footer_text: '', legal_mentions: 'TVA non applicable, art. 293B du CGI',
    bank_details: '', use_logo: true, accent_color: '#E8734A', default_comments: '',
  });
  const [editingAccount, setEditingAccount] = useState(null);
  const [editingTva, setEditingTva] = useState(null);

  const fetchAll = async () => {
    try {
      const [cl, inv, tva, acc, settings] = await Promise.all([
        clientsApi.getAll().then(r => r.data),
        apiFetch('/api/invoicing/invoices').then(r => r.json()),
        apiFetch('/api/invoicing/tva-rates').then(r => r.json()),
        apiFetch('/api/invoicing/accounts').then(r => r.json()),
        apiFetch('/api/invoicing/settings').then(r => r.json()).catch(() => ({})),
      ]);
      setClients(cl); setInvoices(inv); setTvaRates(tva); setAccounts(acc);
      if (settings && settings.prefix) setInvoiceSettings(s => ({ ...s, ...settings }));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  // Invoice actions
  const handleGenerateInvoice = async () => {
    if (!invoiceForm.client_id || !invoiceForm.period_start || !invoiceForm.period_end) return;
    setGenerating(true);
    try {
      const res = await apiJson('/api/invoicing/invoices', 'POST', invoiceForm);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success(`Facture ${data.number} generee`);
      setInvoiceDialog(false);
      fetchAll();
    } catch (e) { toast.error(e.message); } finally { setGenerating(false); }
  };

  const handleUpdateStatus = async (id, status) => {
    await apiJson(`/api/invoicing/invoices/${id}/status`, 'PUT', { status });
    toast.success('Statut mis a jour');
    fetchAll();
  };

  const handleExportInvoice = async (id, format = 'excel') => {
    try {
      const endpoint = format === 'pdf' ? 'export-pdf' : 'export-excel';
      const res = await apiFetch(`/api/invoicing/invoices/${id}/${endpoint}`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `facture_${id}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erreur export'); }
  };

  const handleDeleteInvoice = async (id) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    await apiFetch(`/api/invoicing/invoices/${id}`, { method: 'DELETE' });
    toast.success('Facture supprimee'); fetchAll();
  };

  const openEditInvoice = (inv) => {
    setEditingInvoice(inv);
    setEditLines((inv.lines || []).map((l, i) => ({ ...l, _idx: i })));
    setEditComments(inv.comments || '');
  };

  const updateEditLine = (idx, field, value) => {
    setEditLines(prev => prev.map((l, i) => {
      if (i !== idx) return l;
      const updated = { ...l, [field]: value };
      const qty = parseFloat(updated.quantity) || 0;
      const up = parseFloat(updated.unit_price) || 0;
      const disc = parseFloat(updated.discount) || 0;
      const tva = parseFloat(updated.tva_rate) || 20;
      const htBefore = qty * up;
      const discAmt = htBefore * disc / 100;
      updated.amount_ht = Math.round((htBefore - discAmt) * 100) / 100;
      updated.tva_amount = Math.round(updated.amount_ht * tva / 100 * 100) / 100;
      updated.amount_ttc = Math.round((updated.amount_ht + updated.tva_amount) * 100) / 100;
      return updated;
    }));
  };

  const addEditLine = () => {
    setEditLines(prev => [...prev, { designation: '', quantity: 1, unit: 'jour', unit_price: 0, tva_rate: 20, discount: 0, amount_ht: 0, tva_amount: 0, amount_ttc: 0 }]);
  };

  const removeEditLine = (idx) => {
    setEditLines(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSaveInvoice = async () => {
    if (!editingInvoice) return;
    try {
      const res = await apiJson(`/api/invoicing/invoices/${editingInvoice.id}`, 'PUT', { lines: editLines, comments: editComments });
      if (!res.ok) throw new Error();
      toast.success('Facture mise a jour');
      setEditingInvoice(null);
      fetchAll();
    } catch { toast.error('Erreur sauvegarde'); }
  };

  // TVA actions
  const handleAddTva = async () => {
    try {
      const res = await apiJson('/api/invoicing/tva-rates', 'POST', { ...tvaForm, rate: parseFloat(tvaForm.rate) });
      if (!res.ok) throw new Error();
      toast.success('Taux ajoute'); setTvaDialog(false); setTvaForm({ label: '', rate: '', is_default: false }); fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const handleDeleteTva = async (id) => {
    await apiFetch(`/api/invoicing/tva-rates/${id}`, { method: 'DELETE' });
    toast.success('Supprime'); fetchAll();
  };

  // Account actions
  const handleAddAccount = async () => {
    try {
      const res = await apiJson('/api/invoicing/accounts', 'POST', accountForm);
      if (!res.ok) throw new Error();
      toast.success('Compte ajoute'); setAccountDialog(false); setAccountForm({ code: '', label: '', type: 'credit' }); fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const handleDeleteAccount = async (id) => {
    await apiFetch(`/api/invoicing/accounts/${id}`, { method: 'DELETE' });
    toast.success('Supprime'); fetchAll();
  };

  const handleUpdateAccount = async (id, data) => {
    try {
      await apiJson(`/api/invoicing/accounts/${id}`, 'PUT', data);
      toast.success('Compte mis a jour'); setEditingAccount(null); fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const handleUpdateTva = async (id, data) => {
    try {
      await apiJson(`/api/invoicing/tva-rates/${id}`, 'PUT', data);
      toast.success('Taux mis a jour'); setEditingTva(null); fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const handleSaveInvoiceSettings = async () => {
    try {
      await apiJson('/api/invoicing/settings', 'PUT', invoiceSettings);
      toast.success('Parametres sauvegardes');
    } catch { toast.error('Erreur'); }
  };

  const statusLabels = { draft: 'Brouillon', sent: 'Envoyee', paid: 'Payee', cancelled: 'Annulee' };
  const statusColors = { draft: 'bg-slate-100 text-slate-600', sent: 'bg-blue-100 text-blue-700', paid: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-600' };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;

  return (
    <div className="space-y-4" data-testid="invoicing-page">
      <h1 className="text-xl font-heading font-extrabold text-slate-900">Facturation</h1>

      <Tabs defaultValue="invoices" className="space-y-4">
        <TabsList className="bg-white border flex-wrap">
          <TabsTrigger value="invoices" className="text-xs gap-1.5"><Receipt className="w-3.5 h-3.5" />Factures</TabsTrigger>
          <TabsTrigger value="templates" className="text-xs gap-1.5"><FileText className="w-3.5 h-3.5" />Modeles</TabsTrigger>
          <TabsTrigger value="accounting" className="text-xs gap-1.5"><BookOpen className="w-3.5 h-3.5" />Plan comptable</TabsTrigger>
          <TabsTrigger value="tva" className="text-xs gap-1.5"><Settings className="w-3.5 h-3.5" />TVA</TabsTrigger>
        </TabsList>

        {/* FACTURES */}
        <TabsContent value="invoices">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">{invoices.length} facture(s)</p>
              <Button className="btn-primary" size="sm" onClick={() => setInvoiceDialog(true)} data-testid="new-invoice-btn">
                <Plus size={14} className="mr-1" />Generer une facture
              </Button>
            </div>
            <Card className="card-surface">
              <CardContent className="p-0">
                {invoices.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Aucune facture</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead><tr className="border-b bg-slate-50">
                      <th className="px-4 py-2.5 text-left text-xs font-semibold">N</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold">Client</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold">Periode</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold">HT</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold">TVA</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold">TTC</th>
                      <th className="px-4 py-2.5 text-center text-xs font-semibold">Statut</th>
                      <th className="px-4 py-2.5 text-center text-xs font-semibold w-32">Actions</th>
                    </tr></thead>
                    <tbody>
                      {invoices.map(inv => (
                        <tr key={inv.id} className="border-b hover:bg-slate-50" data-testid={`invoice-${inv.id}`}>
                          <td className="px-4 py-2.5 font-mono text-xs font-bold text-blue-700">{inv.number}</td>
                          <td className="px-4 py-2.5 font-medium text-sm">{inv.client_name}</td>
                          <td className="px-4 py-2.5 text-xs text-slate-500">
                            {inv.period_start ? new Date(inv.period_start).toLocaleDateString('fr-FR') : ''} - {inv.period_end ? new Date(inv.period_end).toLocaleDateString('fr-FR') : ''}
                          </td>
                          <td className="px-4 py-2.5 text-right font-medium">{(inv.total_ht || 0).toFixed(2)} EUR</td>
                          <td className="px-4 py-2.5 text-right text-xs text-slate-500">{(inv.total_tva || 0).toFixed(2)}</td>
                          <td className="px-4 py-2.5 text-right font-bold">{(inv.total_ttc || 0).toFixed(2)} EUR</td>
                          <td className="px-4 py-2.5 text-center">
                            <Select value={inv.status} onValueChange={v => handleUpdateStatus(inv.id, v)}>
                              <SelectTrigger className="h-7 w-28 text-[10px]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="draft">Brouillon</SelectItem>
                                <SelectItem value="sent">Envoyee</SelectItem>
                                <SelectItem value="paid">Payee</SelectItem>
                                <SelectItem value="cancelled">Annulee</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {inv.status === 'draft' && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditInvoice(inv)} title="Modifier"><Pencil size={12} /></Button>}
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleExportInvoice(inv.id, 'excel')} title="Excel"><Download className="w-3.5 h-3.5" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => handleExportInvoice(inv.id, 'pdf')} title="PDF"><FileText className="w-3.5 h-3.5" /></Button>
                              {inv.status === 'draft' && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteInvoice(inv.id)}><Trash size={12} /></Button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Generate Invoice Dialog */}
          <Dialog open={invoiceDialog} onOpenChange={setInvoiceDialog}>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Generer une facture</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-3">
                <div className="space-y-1">
                  <Label className="text-xs">Client *</Label>
                  <Select value={invoiceForm.client_id} onValueChange={v => setInvoiceForm(p => ({ ...p, client_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Selectionner" /></SelectTrigger>
                    <SelectContent>{clients.filter(c => c.billable !== false).map(c => <SelectItem key={c.id} value={c.id}>{c.code} - {c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Debut *</Label><Input type="date" value={invoiceForm.period_start} onChange={e => setInvoiceForm(p => ({ ...p, period_start: e.target.value }))} /></div>
                  <div className="space-y-1"><Label className="text-xs">Fin *</Label><Input type="date" value={invoiceForm.period_end} onChange={e => setInvoiceForm(p => ({ ...p, period_end: e.target.value }))} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Nature de la transaction</Label>
                    <Select value={invoiceForm.nature} onValueChange={v => setInvoiceForm(p => ({ ...p, nature: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Prestation de service">Prestation de service</SelectItem>
                        <SelectItem value="Vente de marchandise">Vente de marchandise</SelectItem>
                        <SelectItem value="Prestation mixte">Prestation mixte</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Type</Label>
                    <Select value={invoiceForm.is_proforma ? 'proforma' : 'invoice'} onValueChange={v => setInvoiceForm(p => ({ ...p, is_proforma: v === 'proforma' }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="invoice">Facture</SelectItem>
                        <SelectItem value="proforma">Pro-forma</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Adresse de livraison / realisation (si differente)</Label>
                  <Input value={invoiceForm.delivery_address} onChange={e => setInvoiceForm(p => ({ ...p, delivery_address: e.target.value }))} placeholder="Optionnel" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Commentaires</Label>
                  <Textarea value={invoiceForm.comments} onChange={e => setInvoiceForm(p => ({ ...p, comments: e.target.value }))} rows={2} placeholder="Notes pour la facture..." />
                </div>
                <p className="text-[10px] text-slate-500">Lignes generees automatiquement depuis le suivi temps + TJM. Vous pourrez les modifier apres generation.</p>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => setInvoiceDialog(false)}>Annuler</Button>
                  <Button className="btn-primary" size="sm" onClick={handleGenerateInvoice} disabled={generating || !invoiceForm.client_id}>{generating ? 'Generation...' : 'Generer'}</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Edit Invoice Dialog - Lines editable */}
          <Dialog open={!!editingInvoice} onOpenChange={o => !o && setEditingInvoice(null)}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Modifier {editingInvoice?.number}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-3">
                {/* Lines table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-slate-100 border-b">
                      <th className="px-2 py-2 text-left w-[30%]">Designation</th>
                      <th className="px-2 py-2 text-center w-14">Qte</th>
                      <th className="px-2 py-2 text-center w-16">Unite</th>
                      <th className="px-2 py-2 text-right w-20">P.U. HT</th>
                      <th className="px-2 py-2 text-right w-16">Remise %</th>
                      <th className="px-2 py-2 text-right w-16">TVA %</th>
                      <th className="px-2 py-2 text-right w-20">HT</th>
                      <th className="px-2 py-2 text-right w-20">TTC</th>
                      <th className="px-2 py-2 w-8"></th>
                    </tr></thead>
                    <tbody>
                      {editLines.map((line, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="px-1 py-1"><Input className="h-7 text-xs" value={line.designation || ''} onChange={e => updateEditLine(idx, 'designation', e.target.value)} /></td>
                          <td className="px-1 py-1"><Input className="h-7 text-xs text-center w-14" type="number" step="0.5" value={line.quantity || 0} onChange={e => updateEditLine(idx, 'quantity', e.target.value)} /></td>
                          <td className="px-1 py-1">
                            <select className="h-7 text-xs border rounded px-1 w-16" value={line.unit || 'jour'} onChange={e => updateEditLine(idx, 'unit', e.target.value)}>
                              <option value="jour">jour</option><option value="heure">heure</option><option value="unite">unite</option><option value="forfait">forfait</option>
                            </select>
                          </td>
                          <td className="px-1 py-1"><Input className="h-7 text-xs text-right w-20" type="number" step="0.01" value={line.unit_price || 0} onChange={e => updateEditLine(idx, 'unit_price', e.target.value)} /></td>
                          <td className="px-1 py-1"><Input className="h-7 text-xs text-right w-16" type="number" step="0.5" value={line.discount || 0} onChange={e => updateEditLine(idx, 'discount', e.target.value)} /></td>
                          <td className="px-1 py-1"><Input className="h-7 text-xs text-right w-16" type="number" step="0.1" value={line.tva_rate || 20} onChange={e => updateEditLine(idx, 'tva_rate', e.target.value)} /></td>
                          <td className="px-1 py-1 text-right font-medium">{(line.amount_ht || 0).toFixed(2)}</td>
                          <td className="px-1 py-1 text-right font-bold">{(line.amount_ttc || 0).toFixed(2)}</td>
                          <td className="px-1 py-1"><Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => removeEditLine(idx)}><Trash size={10} /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Button variant="outline" size="sm" onClick={addEditLine}><Plus size={12} className="mr-1" />Ajouter une ligne</Button>

                {/* Totals */}
                <div className="flex justify-end">
                  <div className="text-sm space-y-1 w-48">
                    <div className="flex justify-between"><span>Total HT:</span><span className="font-medium">{editLines.reduce((s, l) => s + (l.amount_ht || 0), 0).toFixed(2)} EUR</span></div>
                    <div className="flex justify-between"><span>Total TVA:</span><span>{editLines.reduce((s, l) => s + (l.tva_amount || 0), 0).toFixed(2)} EUR</span></div>
                    <div className="flex justify-between font-bold text-blue-700 border-t pt-1"><span>Total TTC:</span><span>{editLines.reduce((s, l) => s + (l.amount_ttc || 0), 0).toFixed(2)} EUR</span></div>
                  </div>
                </div>

                {/* Comments */}
                <div className="space-y-1">
                  <Label className="text-xs">Commentaires / Notes</Label>
                  <Textarea value={editComments} onChange={e => setEditComments(e.target.value)} rows={3} placeholder="Commentaires visibles sur la facture..." />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => setEditingInvoice(null)}>Annuler</Button>
                  <Button className="btn-primary" size="sm" onClick={handleSaveInvoice}>Enregistrer</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* PLAN COMPTABLE - editable */}
        <TabsContent value="accounting">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">{accounts.length} compte(s)</p>
              <Button variant="outline" size="sm" onClick={() => setAccountDialog(true)}><Plus size={14} className="mr-1" />Ajouter un compte</Button>
            </div>
            <Card className="card-surface">
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-slate-50">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold">Code</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold">Libelle</th>
                    <th className="px-4 py-2.5 text-center text-xs font-semibold">Type</th>
                    <th className="px-4 py-2.5 text-center text-xs font-semibold w-24">Actions</th>
                  </tr></thead>
                  <tbody>
                    {accounts.map(a => editingAccount === a.id ? (
                      <tr key={a.id} className="border-b bg-blue-50">
                        <td className="px-4 py-1"><Input className="h-8 text-xs font-mono" defaultValue={a.code} id={`acc-code-${a.id}`} /></td>
                        <td className="px-4 py-1"><Input className="h-8 text-xs" defaultValue={a.label} id={`acc-label-${a.id}`} /></td>
                        <td className="px-4 py-1 text-center">
                          <select className="h-8 text-xs border rounded px-2" defaultValue={a.type} id={`acc-type-${a.id}`}>
                            <option value="debit">Debit</option><option value="credit">Credit</option>
                          </select>
                        </td>
                        <td className="px-4 py-1 text-center">
                          <div className="flex justify-center gap-1">
                            <Button size="sm" className="h-7 text-[10px]" onClick={() => handleUpdateAccount(a.id, {
                              code: document.getElementById(`acc-code-${a.id}`).value,
                              label: document.getElementById(`acc-label-${a.id}`).value,
                              type: document.getElementById(`acc-type-${a.id}`).value,
                            })}>OK</Button>
                            <Button variant="ghost" size="sm" className="h-7 text-[10px]" onClick={() => setEditingAccount(null)}>X</Button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={a.id} className="border-b hover:bg-slate-50">
                        <td className="px-4 py-2 font-mono text-sm font-bold text-blue-700">{a.code}</td>
                        <td className="px-4 py-2 text-sm">{a.label}</td>
                        <td className="px-4 py-2 text-center"><Badge className="text-[10px] bg-slate-100">{a.type}</Badge></td>
                        <td className="px-4 py-2 text-center">
                          <div className="flex justify-center gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingAccount(a.id)}><Pencil size={12} /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteAccount(a.id)}><Trash size={12} /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
            <Dialog open={accountDialog} onOpenChange={setAccountDialog}>
              <DialogContent className="max-w-sm">
                <DialogHeader><DialogTitle>Ajouter un compte</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-3">
                  <div className="space-y-1"><Label className="text-xs">Code *</Label><Input value={accountForm.code} onChange={e => setAccountForm(p => ({ ...p, code: e.target.value }))} placeholder="706000" /></div>
                  <div className="space-y-1"><Label className="text-xs">Libelle *</Label><Input value={accountForm.label} onChange={e => setAccountForm(p => ({ ...p, label: e.target.value }))} placeholder="Prestations de services" /></div>
                  <div className="space-y-1"><Label className="text-xs">Type</Label>
                    <Select value={accountForm.type} onValueChange={v => setAccountForm(p => ({ ...p, type: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="debit">Debit</SelectItem><SelectItem value="credit">Credit</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => setAccountDialog(false)}>Annuler</Button>
                    <Button className="btn-primary" size="sm" onClick={handleAddAccount} disabled={!accountForm.code || !accountForm.label}>Ajouter</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </TabsContent>

        {/* TVA - editable */}
        <TabsContent value="tva">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">{tvaRates.length} taux configure(s)</p>
              <Button variant="outline" size="sm" onClick={() => setTvaDialog(true)}><Plus size={14} className="mr-1" />Ajouter un taux</Button>
            </div>
            <Card className="card-surface">
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-slate-50">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold">Libelle</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold">Taux</th>
                    <th className="px-4 py-2.5 text-center text-xs font-semibold">Defaut</th>
                    <th className="px-4 py-2.5 text-center text-xs font-semibold w-24">Actions</th>
                  </tr></thead>
                  <tbody>
                    {tvaRates.map(r => editingTva === r.id ? (
                      <tr key={r.id} className="border-b bg-blue-50">
                        <td className="px-4 py-1"><Input className="h-8 text-xs" defaultValue={r.label} id={`tva-label-${r.id}`} /></td>
                        <td className="px-4 py-1 text-right"><Input className="h-8 text-xs w-20 ml-auto" type="number" step="0.1" defaultValue={r.rate} id={`tva-rate-${r.id}`} /></td>
                        <td className="px-4 py-1 text-center"><input type="checkbox" defaultChecked={r.is_default} id={`tva-def-${r.id}`} /></td>
                        <td className="px-4 py-1 text-center">
                          <div className="flex justify-center gap-1">
                            <Button size="sm" className="h-7 text-[10px]" onClick={() => handleUpdateTva(r.id, {
                              label: document.getElementById(`tva-label-${r.id}`).value,
                              rate: parseFloat(document.getElementById(`tva-rate-${r.id}`).value),
                              is_default: document.getElementById(`tva-def-${r.id}`).checked,
                            })}>OK</Button>
                            <Button variant="ghost" size="sm" className="h-7 text-[10px]" onClick={() => setEditingTva(null)}>X</Button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={r.id} className="border-b hover:bg-slate-50">
                        <td className="px-4 py-2 text-sm font-medium">{r.label}</td>
                        <td className="px-4 py-2 text-right"><Badge className="bg-blue-50 text-blue-700">{r.rate}%</Badge></td>
                        <td className="px-4 py-2 text-center">{r.is_default && <Badge className="bg-green-50 text-green-700 text-[10px]">Defaut</Badge>}</td>
                        <td className="px-4 py-2 text-center">
                          <div className="flex justify-center gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingTva(r.id)}><Pencil size={12} /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteTva(r.id)}><Trash size={12} /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
            <Dialog open={tvaDialog} onOpenChange={setTvaDialog}>
              <DialogContent className="max-w-sm">
                <DialogHeader><DialogTitle>Ajouter un taux TVA</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-3">
                  <div className="space-y-1"><Label className="text-xs">Libelle *</Label><Input value={tvaForm.label} onChange={e => setTvaForm(p => ({ ...p, label: e.target.value }))} placeholder="TVA 20%" /></div>
                  <div className="space-y-1"><Label className="text-xs">Taux (%) *</Label><Input type="number" step="0.1" value={tvaForm.rate} onChange={e => setTvaForm(p => ({ ...p, rate: e.target.value }))} placeholder="20" /></div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => setTvaDialog(false)}>Annuler</Button>
                    <Button className="btn-primary" size="sm" onClick={handleAddTva} disabled={!tvaForm.label || !tvaForm.rate}>Ajouter</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </TabsContent>

        {/* MODELES FACTURE */}
        <TabsContent value="templates">
          <div className="space-y-4">
            <Card className="card-surface">
              <CardHeader className="py-3 px-4"><CardTitle className="text-sm">Parametres de facturation</CardTitle></CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Prefixe facture</Label>
                    <Input value={invoiceSettings.prefix} onChange={e => setInvoiceSettings(p => ({ ...p, prefix: e.target.value }))} placeholder="FAC" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Prochain numero</Label>
                    <Input type="number" value={invoiceSettings.next_number} onChange={e => setInvoiceSettings(p => ({ ...p, next_number: parseInt(e.target.value) || 1 }))} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Conditions de paiement</Label>
                  <Input value={invoiceSettings.payment_terms} onChange={e => setInvoiceSettings(p => ({ ...p, payment_terms: e.target.value }))} placeholder="Paiement a 30 jours fin de mois" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Mentions legales</Label>
                  <Textarea value={invoiceSettings.legal_mentions} onChange={e => setInvoiceSettings(p => ({ ...p, legal_mentions: e.target.value }))} rows={2} placeholder="TVA non applicable, art. 293B du CGI" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Coordonnees bancaires (RIB/IBAN)</Label>
                  <Textarea value={invoiceSettings.bank_details} onChange={e => setInvoiceSettings(p => ({ ...p, bank_details: e.target.value }))} rows={2} placeholder="IBAN: FR76 XXXX XXXX XXXX&#10;BIC: XXXXX" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Texte de pied de page</Label>
                  <Textarea value={invoiceSettings.footer_text} onChange={e => setInvoiceSettings(p => ({ ...p, footer_text: e.target.value }))} rows={2} placeholder="Merci pour votre confiance..." />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={invoiceSettings.use_logo} onChange={e => setInvoiceSettings(p => ({ ...p, use_logo: e.target.checked }))} id="use-logo" />
                  <Label htmlFor="use-logo" className="text-xs cursor-pointer">Inclure le logo societe sur les factures PDF</Label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Couleur d'accent (header tableau, totaux)</Label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={invoiceSettings.accent_color || '#E8734A'} onChange={e => setInvoiceSettings(p => ({ ...p, accent_color: e.target.value }))} className="w-10 h-8 rounded cursor-pointer" />
                      <Input value={invoiceSettings.accent_color || '#E8734A'} onChange={e => setInvoiceSettings(p => ({ ...p, accent_color: e.target.value }))} className="h-8 font-mono text-xs w-28" />
                    </div>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Commentaires par defaut (ajoutes automatiquement sur chaque facture)</Label>
                  <Textarea value={invoiceSettings.default_comments} onChange={e => setInvoiceSettings(p => ({ ...p, default_comments: e.target.value }))} rows={2} placeholder="Merci pour votre confiance..." />
                </div>
                <div className="flex justify-end pt-2">
                  <Button className="btn-primary" size="sm" onClick={handleSaveInvoiceSettings} data-testid="save-invoice-settings">Enregistrer</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

      </Tabs>
    </div>
  );
}
